import { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import PdfGenerator, { PdfGeneratorHandler } from "../../components/PdfGenerator";
import { SignatureModal, SignatureModalRef } from "./SignatureModal";
import { Api } from "../../api/adress";
import { ExButton } from "../../components/ExButton";
import { FileCheck } from "lucide-react";
import { formatDateTime, toastError } from "../../commons/utils";
import LogoExalt from "../../images/logoPequenaLigth.png";
import { ExCadastroPadrao } from "../../components/ExCadastroPadrao";
import { ModalRef } from "../../components/Modal";

const model_motivo = {
    inputs: [
        { id: 'motivo', label: 'Informe o motivo do rejeição', required: true, type: 'text', md: 12, textarea: true, rows: 3 },
    ]
}

const Page = styled.div`
  height: 100vh;
  background:#F8F9FA;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: #fff;
  padding: 24px 32px;
`;

const Title = styled.h4`
  margin: 0;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #3d3d3d;
  font-size: 14px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const DocumentCard = styled.div`
  background: #fff;
  width: 100%;
  max-width: 640px;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const CardTitle = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
  font-size: 14px;
  color: #3d3d3d;
`;

const Footer = styled.div`
  background: #fff;
  padding: 20px 32px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const StatusBox = styled.div<{ status: "signed" | "available" }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;

  ${({ status }) =>
        status === "signed" && `background: rgba(3, 85, 0, 0.74);`}
  ${({ status }) =>
        status === "available" && `background: rgba(12, 117, 255, 0.712);`}
`;

const WarningBox = styled.div`
  margin-top: 20px;
  padding: 14px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 600;
  white-space: pre-line;
`;

export function PdfSigner() {
    const refSign = useRef<SignatureModalRef>(null);
    const refMovimentoPdf = useRef<PdfGeneratorHandler>(null);

    const params = new URLSearchParams(window.location.search);
    const movlinkid = params.get("movlinkid");

    const refmodal = useRef<ModalRef>(null);

    const [loading, setLoading] = useState(true);
    const [linkAtivo, setLinkAtivo] = useState(false);

    const [movlinkdatahora, setMovlinkdatahora] = useState<Date | null>(null);
    const [expireAt, setExpireAt] = useState<Date | null>(null);
    const [indisponivelMsg, setIndisponivelMsg] = useState<string | null>(null);
    const [informationMsg, setInformationMsg] = useState<string | null>(null);

    useEffect(() => {
        async function fetchMovimentoLink() {
            try {
                const response = await Api.get("movimentolink", {
                    params: { movlinkid },
                });

                const data = response.data;

                if (!data) {
                    setIndisponivelMsg("Link inválido ou inexistente.");
                    return;
                }

                const createdAt = new Date(data.movlinkdatahora);
                const expires = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);
                const now = new Date();

                setMovlinkdatahora(createdAt);
                setExpireAt(expires);

                if (data.movhistid) {
                    setIndisponivelMsg("Este documento já foi processado.");
                    return;
                }

                if (data.movlinkativo !== 1) {
                    setIndisponivelMsg("Este documento não está mais disponível.");
                    return;
                }

                if (now > expires) {
                    setIndisponivelMsg("O prazo para assinar este documento expirou.");
                    return;
                }

                setLinkAtivo(true);
            } catch (error) {
                console.error(error);
                setIndisponivelMsg("Erro ao carregar o documento.");
            } finally {
                setLoading(false);
            }
        }

        fetchMovimentoLink();
    }, [movlinkid]);

    function openPDF() {
        refMovimentoPdf.current?.generatePDF("view", { movlinkid });
    }

    function handleSign() {
        refSign.current?.openModal();
    }

    async function handleReject(values: any) {
        try {
            await Api.post("rejectmovbylink", {
                movlinkid,
                motivo: values.motivo
            });

            setLinkAtivo(false);
            setIndisponivelMsg(
                "❌ Documento rejeitado com sucesso.\n\n" +
                "📝 Motivo registrado e enviado para revisão.\n\n" +
                "ℹ️ Atenção: este link ficará indisponível assim que sair desta página."
            );
        } catch (error: any) {
            console.error(error);
            toastError(error)
        }
    }

    async function handleSignatureSave(signatureBase64: string) {
        const base64Limpa = signatureBase64.replace(/^data:image\/png;base64,/, "");

        await Api.post("acceptmovbylink", {
            movlinkid,
            signdata: base64Limpa,
        });

        setLinkAtivo(false);
        setInformationMsg(
            "✅ Documento assinado com sucesso!\n\n" +
            "📄 Faça o download do documento assinado utilizando o botão acima.\n\n" +
            "ℹ️ Atenção: este link ficará indisponível assim que sair desta página."
        );
    }

    if (loading) {
        return <div style={{ padding: 32 }}>Carregando documento...</div>;
    }

    const status: "signed" | "available" = linkAtivo ? "available" : "signed";

    return (
        <Page>
            <PdfGenerator ref={refMovimentoPdf} api="/movimentopdfgeneratepub" />

            <Header>
                <Title>Assinatura de Documento</Title>
                <Subtitle>
                    Leia atentamente o documento antes de prosseguir com a assinatura
                </Subtitle>
            </Header>

            <Content>
                <DocumentCard>
                    <CardTitle>
                        <img src={LogoExalt} alt="logo_exalt" width={200} />
                    </CardTitle>

                    {!indisponivelMsg &&
                        <>
                            <ExButton icon={<FileCheck />} size="lg" onClick={openPDF} style={{ width: "100%", alignItems: 'center', justifyContent: 'center' }}>
                                Visualizar Documento
                            </ExButton>

                            {!informationMsg &&
                                <InfoRow>
                                    <span>Status</span>
                                    <StatusBox status={status}>
                                        {status === "signed" ? "Documento assinado" : "Disponível para assinatura"}
                                    </StatusBox>
                                </InfoRow>
                            }

                            {!informationMsg && movlinkdatahora && (
                                <InfoRow>
                                    <span>Criado em</span>
                                    <strong>{formatDateTime(movlinkdatahora)}</strong>
                                </InfoRow>
                            )}

                            {!informationMsg &&
                                <InfoRow>
                                    <span>Expira em</span>
                                    <strong>{formatDateTime(expireAt)}</strong>
                                </InfoRow>
                            }
                        </>
                    }
                    {indisponivelMsg && <WarningBox style={{ background: 'rgba(180, 36, 36, 0.12)', color: '#b42424' }}>{indisponivelMsg}</WarningBox>}
                    {informationMsg && <WarningBox style={{ background: 'rgba(12, 117, 255, 0.12)', color: '#0c3b7a' }}>{informationMsg}</WarningBox>}
                </DocumentCard>
            </Content>


            <Footer>
                {!indisponivelMsg &&
                    <ExButton size="lg" onClick={handleSign} disabled={!linkAtivo} color="#1e701b">
                        Assinar
                    </ExButton>
                }
                {!indisponivelMsg &&
                    <ExButton size="lg" onClick={() => refmodal.current?.openModal()} disabled={!linkAtivo} color="#b42424">
                        Rejeitar
                    </ExButton>
                }
            </Footer>

            <SignatureModal ref={refSign} onSave={handleSignatureSave} />

            <ExCadastroPadrao ref={refmodal} model={model_motivo} success={(values: any) => handleReject(values)} />
        </Page>
    );
}
